import React from 'react';

import { FolderIcon } from '@vlabs/icons';
import { Badge, SideMenu, useWindowSize } from '@vlabs/uikit';
import { useWatch } from 'react-hook-form';

import { useSearchPageTranslation } from '../../../i18n';
import { getEventFilterActivity } from '../filterActivity';
import st from '../Filters.module.sass';
import { EventFiltersWithLeftPopover, EventFiltersWithRightPopover } from '../side-menu';

export interface EventFilterFormProps {
  includedAttributes: string[];
}

function getGroupBadge(count: number): React.JSX.Element | undefined {
  if (count === 0) return undefined;
  return <Badge value={count} />;
}

function EventFilterForm(props: EventFilterFormProps): React.JSX.Element {
  const { t } = useSearchPageTranslation();
  const { includedAttributes = [] } = props;
  const { width } = useWindowSize();
  // @ts-expect-error требуется типизация useWindowSize
  const EventFilters = width < 1920 ? EventFiltersWithLeftPopover : EventFiltersWithRightPopover;

  const { perFilter, counts } = getEventFilterActivity(useWatch());

  return (
    <React.Fragment>
      <SideMenu.MenuItem
        className={st.MenuGroup}
        data-testid="events.commonFilters"
        leftIcon={<FolderIcon />}
        rightIcon={getGroupBadge(counts.general)}
        value={t('filters.event_filters.general.title')}
      >
        <EventFilters.General.DateFilter
          badge={perFilter.date ? <Badge shape="dot" /> : undefined}
          className={st.MenuGroup}
          testId="dateFilter"
          value={t('filters.event_filters.general.creation_date')}
        />
        <EventFilters.General.VideoSourceFilter
          badge={perFilter.sources ? <Badge shape="dot" /> : undefined}
          className={st.MenuGroup}
          testId="videoSourceFilter"
          value={t('filters.event_filters.general.video_source')}
        />
        <EventFilters.General.HandlersFilter
          badge={perFilter.handlers ? <Badge shape="dot" /> : undefined}
          className={st.MenuGroup}
          testId="handlersFilter"
          value={t('filters.event_filters.general.handlers')}
        />
        <EventFilters.General.SimilarityFilter
          badge={perFilter.similarity ? <Badge shape="dot" /> : undefined}
          className={st.MenuGroup}
          testId="similarityFilter"
          value={t('filters.event_filters.general.similarity')}
        />
      </SideMenu.MenuItem>
      <div className={st.Divider} />
      <SideMenu.MenuItem
        className={st.MenuGroup}
        leftIcon={<FolderIcon />}
        rightIcon={getGroupBadge(counts.comparing)}
        value={t('filters.event_filters.comparing_attributes.title')}
      >
        <EventFilters.Comparing.ComparingAttributeFilter
          badge={perFilter.comparingAttribute ? <Badge shape="dot" /> : undefined}
          className={st.MenuGroup}
          testId="comparingAttributeFilter"
          value={t('filters.event_filters.comparing_attributes.comparing_attribute')}
        />
        <EventFilters.Comparing.TopSimilarObjectsFilter
          badge={perFilter.topSimilarObjects ? <Badge shape="dot" /> : undefined}
          className={st.MenuGroup}
          testId="topSimilarObjectsFilter"
          value={t('filters.event_filters.comparing_attributes.top_similar_objects')}
        />
      </SideMenu.MenuItem>
      <div className={st.Divider} />
      <SideMenu.MenuItem
        className={st.MenuGroup}
        data-testid="events.faceAttributesFilters"
        leftIcon={<FolderIcon />}
        rightIcon={getGroupBadge(counts.face)}
        value={t('filters.event_filters.face_attributes.title')}
      >
        <EventFilters.Face.GenderFilter
          badge={perFilter.gender ? <Badge shape="dot" /> : undefined}
          className={st.MenuGroup}
          testId="genderFilter"
          value={t('filters.event_filters.face_attributes.gender')}
        />
        <EventFilters.Face.AgeFilter
          badge={perFilter.age ? <Badge shape="dot" /> : undefined}
          className={st.MenuGroup}
          testId="ageFilter"
          value={t('filters.event_filters.face_attributes.age')}
        />
        <EventFilters.Face.EmotionFilter
          badge={perFilter.emotion ? <Badge shape="dot" /> : undefined}
          className={st.MenuGroup}
          testId="emotionFilter"
          value={t('filters.event_filters.face_attributes.emotion')}
        />
        <EventFilters.Face.MasksFilter
          badge={perFilter.masks ? <Badge shape="dot" /> : undefined}
          className={st.MenuGroup}
          testId="masksFilter"
          value={t('filters.event_filters.face_attributes.mask')}
        />
        <EventFilters.Face.LivenessFilter
          badge={perFilter.liveness ? <Badge shape="dot" /> : undefined}
          className={st.MenuGroup}
          testId="livenessFilter"
          value={t('filters.event_filters.face_attributes.liveness')}
        />
        <EventFilters.Face.DeepfakeFilter
          badge={perFilter.deepfake ? <Badge shape="dot" /> : undefined}
          className={st.MenuGroup}
          testId="deepfakeFilter"
          value={t('filters.event_filters.face_attributes.deepfake')}
        />
        <EventFilters.Face.FaceIdsFilter
          badge={perFilter.faceIds ? <Badge shape="dot" /> : undefined}
          className={st.MenuGroup}
          testId="faceIdsFilter"
          value={t('filters.event_filters.face_attributes.face_ids')}
        />
      </SideMenu.MenuItem>
      <div className={st.Divider} />
      <SideMenu.MenuItem
        className={st.MenuGroup}
        leftIcon={<FolderIcon />}
        rightIcon={getGroupBadge(counts.body)}
        value={t('filters.event_filters.body_attributes.title')}
      >
        <EventFilters.Body.ApparentGenderFilter
          badge={perFilter.apparentGender ? <Badge shape="dot" /> : undefined}
          className={st.MenuGroup}
          testId="apparentGenderFilter"
          value={t('filters.event_filters.body_attributes.gender')}
        />
        <EventFilters.Body.ApparentAgeFilter
          badge={perFilter.apparentAge ? <Badge shape="dot" /> : undefined}
          className={st.MenuGroup}
          testId="apparentAgeFilter"
          value={t('filters.event_filters.body_attributes.age')}
        />
        <EventFilters.Body.UpperAttributesFilter
          badge={perFilter.upper ? <Badge shape="dot" /> : undefined}
          className={st.MenuGroup}
          testId="upperAttributesFilter"
          value={t('filters.event_filters.body_attributes.upper_attributes')}
        />
        <EventFilters.Body.LowerAttributesFilter
          badge={perFilter.lower ? <Badge shape="dot" /> : undefined}
          className={st.MenuGroup}
          testId="lowerAttributesFilter"
          value={t('filters.event_filters.body_attributes.lower_attributes')}
        />
        <EventFilters.Body.ShoesFilter
          badge={perFilter.shoes ? <Badge shape="dot" /> : undefined}
          className={st.MenuGroup}
          testId="shoesFilter"
          value={t('filters.event_filters.body_attributes.shoes')}
        />
        <EventFilters.Body.HeadWearFilter
          badge={perFilter.headwear ? <Badge shape="dot" /> : undefined}
          className={st.MenuGroup}
          testId="headWearFilter"
          value={t('filters.event_filters.body_attributes.head_wear')}
        />
        <EventFilters.Body.BackpackFilter
          badge={perFilter.backpack ? <Badge shape="dot" /> : undefined}
          className={st.MenuGroup}
          testId="backpackFilter"
          value={t('filters.event_filters.body_attributes.backpack')}
        />
      </SideMenu.MenuItem>
      <div className={st.Divider} />
      <EventFilters.Location.LocationFilter
        badge={getGroupBadge(perFilter.location ? 1 : 0)}
        className={st.MenuGroup}
        leftIcon={<FolderIcon />}
        popoverProps={{ includedAttributes }}
        testId="locationFilter"
        value={t('filters.event_filters.location.title')}
      />
    </React.Fragment>
  );
}

export { EventFilterForm };
